// This class provides a queuing system for fetch calls, to limit inflight API requests, for example

interface QueueItem {
  url: string;
  init?: RequestInit;
  resolve: (value: Response | PromiseLike<Response>) => void;
  reject: (reason?: any) => void;
  bypassCache: boolean;
}

interface CacheItem {
  response: Response;
  timestamp: number;
}

export default class FetchQueue {
  private concurrency: number;
  private queue: QueueItem[];
  private running: number;
  private cache: Map<string, CacheItem>;
  private cacheExpiration: number;
  private cleanupInterval: number;
  private inflightRequests: Map<string, Promise<Response>>;

  // concurrency controls how many requests will be allowed inflight at a time
  // cacheExpiration sets the cache duration, defaulting to 5 minutes
  constructor(concurrency: number = 2, cacheExpiration: number = 5 * 60 * 1000) {
    this.concurrency = concurrency;
    this.queue = [];
    this.running = 0;
    this.cache = new Map();
    this.cacheExpiration = cacheExpiration;
    this.inflightRequests = new Map();

    // Run cleanup every minute
    this.cleanupInterval = window.setInterval(() => this.cleanupCache(), 60 * 1000);
  }

  private getCacheKey(url: string, init?: RequestInit, useBody: boolean = true): string {
    while(url.endsWith('?')) {
      url = url.slice(0, -1);
    }

    if(init?.body && useBody) {
      return `${url}:${JSON.stringify(init.body || '')}`;
    } else {
      return `${url}:`;
    }
  }

  private isCacheValid(cacheItem: CacheItem): boolean {
    return Date.now() - cacheItem.timestamp < this.cacheExpiration;
  }

  private cleanupCache(): void {
    const now = Date.now();
    for (const [key, item] of this.cache) {
      if (now - item.timestamp >= this.cacheExpiration) {
        this.cache.delete(key);
      }
    }
  }

  // Provide url and init, just like fetch.
  // If the third arg, bypassQueue, is === true, we will NOT queue this request, and just immediately run fetch
  enqueue(url: string, init?: RequestInit, bypassQueue: boolean = false, bypassCache: boolean = false): Promise<Response> {
    if(bypassQueue === true) {
      return this.fetchAndCache(url, init, bypassCache);
    }

    return new Promise<Response>((resolve, reject) => {
      this.queue.push({ url, init, resolve, reject, bypassCache });
      this.dequeue();
    });
  }

  removeCachesMatching(urlPart: string) {
    for (const [key] of this.cache) {
      if (key.includes(urlPart)) {
        this.cache.delete(key);
      }
    }
  }

  private extractObjParts(url: string): string[] | null {
    try {
        const urlObj = new URL(url);
        const path = urlObj.pathname;

        if(path.startsWith('/api/obj/')) {
          const remainingPath = path.substring('/api/obj/'.length);
          const parts = remainingPath.split('/').filter(part => part.length > 0);
          if(parts.length > 0)
            return parts;
          return null;
        }

        return null;
      } catch(error) {
        // Invalid URL
        return null;
      }
  }

  private getInflightRequestKey(method: string | undefined, cacheKey: string): string {
    return `${method ?? "GET"}+${cacheKey}`;
  }

  private async fetchAndCache(url: string, init?: RequestInit, bypassCache: boolean = false): Promise<Response> {
    const objParts = this.extractObjParts(url);

    const cacheKey = this.getCacheKey(url, init, !objParts);
    if(objParts) {
      // This is an object request. Determine cachability. This will be like ['dashboard', '{ID}'] or ['query'] or ['dashboard_page', 'id', 'entry']
      if(objParts.length === 1) {
        // These are list or search requests
        // We should not cache them, as we have no way of knowing what changed within
        bypassCache = true;
      }

      if(init?.method && init?.method !== "GET") {
        // We're creating or updating an object
        // Wipe any caches to it, and do not cache this
        bypassCache = true;
        //console.debug(`CACHE CLEAR: ${url}`);
        this.cache.delete(cacheKey);

        if(objParts.length === 2) {
          // item + ID, also invalidate entries
          let entriesUrl = url;
          while(entriesUrl.endsWith('?') || entriesUrl.endsWith('/')) {
            entriesUrl = entriesUrl.slice(0, -1);
          }
          entriesUrl = entriesUrl + '/entry';
          //console.debug(`CACHE ENTRIES CLEAR: ${entriesUrl}`);
          this.cache.delete(this.getCacheKey(entriesUrl, init, false));
        }
      }
    }

    if (url.includes('/entry')) {
      // This is an entry request, which should not be cached as we have no way of knowing
      // when a contained resource changes.
      bypassCache = true;
      this.cache.delete(cacheKey);
    }

    if(!bypassCache) {
      const cachedItem = this.cache.get(cacheKey);

      if (cachedItem) {
        if (this.isCacheValid(cachedItem)) {
          //console.debug(`CACHED! ${url}...`);
          return cachedItem.response.clone();
        } else {
          this.cache.delete(cacheKey);
        }
      }

      // Check if there's already an in-flight request for this cache key
      const existingRequest = this.inflightRequests.get(this.getInflightRequestKey(init?.method, cacheKey));
      if (existingRequest) {
        //console.debug(`WAITING FOR IN-FLIGHT REQUEST: ${url}...`);
        return existingRequest.then(response => response.clone());
      }
    }

    // Create the fetch promise and store it in inflightRequests
    const fetchPromise = this.performFetch(url, init, bypassCache, cacheKey);
    this.inflightRequests.set(this.getInflightRequestKey(init?.method, cacheKey), fetchPromise);

    try {
      const response = await fetchPromise;
      return response;
    } finally {
      // Clean up the in-flight request when complete
      this.inflightRequests.delete(this.getInflightRequestKey(init?.method, cacheKey));
    }
  }

  private async performFetch(url: string, init: RequestInit|undefined, bypassCache: boolean, cacheKey: string): Promise<Response> {
    //console.debug(`Fetching ${init?.method || "GET"} ${url} (bypassCache: ${bypassCache})...`);
    const response = await fetch(url, init);

    if (!bypassCache && response && response.ok && (response.status === 200 || response.status === 204)) {
      const clonedResponse = response.clone();
      this.cache.set(cacheKey, {
        response: clonedResponse,
        timestamp: Date.now(),
      });
    }

    return response;
  }

  private dequeue(): void {
    if(this.running >= this.concurrency || this.queue.length === 0) {
        return;
    }

    const { url, init, resolve, reject, bypassCache } = this.queue.shift()!;
    this.running++;

    this.fetchAndCache(url, init, bypassCache).then((response: Response) => {
        this.running--;
        this.dequeue();
        resolve(response);
    }).catch((error: Error) => {
        this.running--;
        this.dequeue();
        reject(error);
    });
  }
}
